import React, { useEffect, useState } from "react";
import { Layout, Menu, Skeleton, Tooltip } from "antd";
import { useMsal } from "@azure/msal-react";
import { useNavigate } from "react-router-dom";
import { getAccessToken } from "config/token";
import axios from "axios";
import { NEXUS_API_BASE_URL } from "const";
import { IDomainsConfig } from "pages/Ponds/types";
import type { MenuProps } from "antd";
import {
  compareNavItemsByLabel,
  DATA_DOMAINS_KEY,
  DATA_DOMAINS_NAV_ITEM,
  ROUTE_NAV_ITEMS,
  SidebarNavItem,
} from "./navConfig";
import "./style.scss";

const { Sider } = Layout;

type MenuItem = Required<MenuProps>["items"][number];

// Custom component for inactive menu items with tooltip
const InactiveMenuItem: React.FC<{ label: React.ReactNode }> = ({ label }) => (
  <Tooltip title={<><strong>{label}</strong> Domain is not yet available</>} placement="right">
    <span style={{ color: "#8c8c8c", cursor: "not-allowed", display: "block", width: "100%" }}>
      {label}
    </span>
  </Tooltip>
);

// Custom component for active menu items with tooltip
const ActiveMenuItem: React.FC<{ label: React.ReactNode }> = ({ label }) => (
  <Tooltip title={<><strong>{label}</strong></>} placement="right">
    <span style={{ display: "block", width: "100%" }}>
      {label}
    </span>
  </Tooltip>
);

/** Builds a nested domain entry, greying out domains that are not yet available. */
function createDomainMenuItem(domain: IDomainsConfig): MenuItem {
  const { display_name: label, active } = domain;
  const isInactive = active === "0";

  return {
    key: label,
    label: isInactive ? <InactiveMenuItem label={label} /> : <ActiveMenuItem label={label} />,
    active,
    className: isInactive ? "inactive-menu-item" : "",
  } as MenuItem;
}

/** Builds a top-level entry, optionally with a nested submenu. */
function createTopLevelMenuItem(
  { key, label, icon, tooltip }: SidebarNavItem,
  children?: MenuItem[],
): MenuItem {
  return {
    key,
    icon,
    children,
    label: (
      <Tooltip title={tooltip ?? <strong>{label}</strong>} placement="right">
        <span style={{ display: "block", width: "100%" }}>{label}</span>
      </Tooltip>
    ),
  } as MenuItem;
}

/** Sorting logic: Active domains first, then inactive */
function sortDomains(domains: IDomainsConfig[]) {
  return [...domains].sort((a, b) => {
    const aActive = a.active === "1" ? 0 : 1;
    const bActive = b.active === "1" ? 0 : 1;
    return aActive - bActive;
  });
}

/**
 * Top-level items are always rendered alphabetically; the nested domain order
 * (active domains first) is intentionally left untouched.
 * Route items come from the static ROUTE_NAV_ITEMS list in navConfig.
 */
function buildMenuItems(domains: IDomainsConfig[]): MenuItem[] {
  const domainChildren = sortDomains(domains).map(createDomainMenuItem);

  return [DATA_DOMAINS_NAV_ITEM, ...ROUTE_NAV_ITEMS]
    .sort(compareNavItemsByLabel)
    .map((navItem) =>
      createTopLevelMenuItem(
        navItem,
        navItem.key === DATA_DOMAINS_KEY ? domainChildren : undefined,
      ),
    );
}

export interface SidebarProps {
  collapsed: boolean;
  collapsedWidth?: number;
  width?: number;
  selectedDomainKey?: string;
  onCollapse: (collapsed: boolean) => void;
  onDomainSelect: (domain: IDomainsConfig) => void;
}

const Sidebar: React.FC<SidebarProps> = ({
  collapsed,
  collapsedWidth = 80,
  width = 300,
  selectedDomainKey,
  onCollapse,
  onDomainSelect,
}) => {
  const msalInstance = useMsal();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [domainConfigs, setDomainConfigs] = useState<IDomainsConfig[]>([]);
  const [menuItems, setMenuItems] = useState<MenuItem[]>([]);
  const navPathByKey = ROUTE_NAV_ITEMS.reduce(
    (paths, { key, path }) => ({ ...paths, [key]: path }),
    {} as Record<string, string>,
  );
  // Data Domains + static route items (drives submenu max-height via CSS var)
  const topLevelItemCount = 1 + ROUTE_NAV_ITEMS.length;

  useEffect(() => {
    const fetchDomains = async () => {
      setLoading(true);
      let domains: IDomainsConfig[] = [];
      const cached = localStorage.getItem("domainsData");
      if (cached) {
        try {
          domains = JSON.parse(cached);
        } catch {
          domains = [];
        }
      }
      if (!domains || !Array.isArray(domains) || domains.length === 0) {
        // Fetch from API
        try {
          const token = await getAccessToken(msalInstance);

          const bearerToken = "Bearer " + (token?.token ?? "");
          const res = await axios.get(
            `${NEXUS_API_BASE_URL}/nexus/get-domains`,
            { headers: { Authorization: bearerToken } },
          );
          domains = res.data;
          localStorage.setItem("domainsData", JSON.stringify(domains));
        } catch (error) {
          console.error("Error fetching domains:", error);
          domains = [];
        }
      }
      setDomainConfigs(domains);
      setMenuItems(buildMenuItems(domains));
      setLoading(false);
    };
    fetchDomains();
  }, [msalInstance]);

  // Recursive search for the clicked item
  const findItem = (items: MenuProps["items"], key: string): any => {
    if (!items) return null;
    for (const item of items) {
      if (!item) continue;
      if (item.key === key) return item;
      if ("children" in item && item.children) {
        const found = findItem(item.children, key);
        if (found) return found;
      }
    }
    return null;
  };

  const handleMenuClick: MenuProps["onClick"] = (info) => {
    const path = navPathByKey[info.key];
    if (path) {
      navigate(path);
      return;
    }

    const clickedItem = findItem(menuItems, info.key);
    const domain = domainConfigs.find((d) => d.display_name === info.key);

    // Skip processing for inactive items (they should be disabled anyway)
    if (clickedItem && clickedItem.active === "0") {
      return;
    }

    if (domain) {
      onDomainSelect(domain);
    }
  };

  // Figure out what to select by default
  const defaultSelectedKeys = selectedDomainKey ? [selectedDomainKey] : [];

  // Helper: check if the domains submenu has been populated. Looked up by key so
  // it stays correct regardless of where alphabetical ordering places the item.
  const isMenuLoaded = () =>
    menuItems.some(
      (item) =>
        item?.key === DATA_DOMAINS_KEY &&
        "children" in item &&
        Array.isArray(item.children) &&
        item.children.length > 0,
    );

  return (
    <Sider
      collapsible
      collapsed={collapsed}
      onCollapse={onCollapse}
      collapsedWidth={collapsedWidth}
      width={width}
      breakpoint="md"
      style={{
        overflow: "hidden",
        height: "100vh",
        position: "fixed",
        left: 0,
        top: 0,
        bottom: 0,
        zIndex: 1,
        // Used by style.scss to size the domains submenu without hardcoding item count
        ["--sidebar-top-level-count" as string]: String(topLevelItemCount),
      }}
    >
      {!collapsed && (loading || !isMenuLoaded()) ? (
        <div style={{ marginTop: "100px", padding: "0 16px" }}>
          <Skeleton active paragraph={{ rows: 10 }} title={false} />
          <Skeleton active paragraph={{ rows: 8 }} title={false} style={{ marginTop: 20 }} />
        </div>
      ) : (
        <div style={{ height: "calc(100vh - 48px)", overflowY: "auto", overflowX: "hidden" }}>
          <Menu
            theme="dark"
            mode="inline"
            inlineCollapsed={collapsed}
            selectedKeys={defaultSelectedKeys}
            defaultSelectedKeys={defaultSelectedKeys}
            defaultOpenKeys={[DATA_DOMAINS_KEY]}
            items={menuItems}
            onClick={handleMenuClick}
            style={{
              marginTop: "100px",
              paddingBottom: "16px",
            }}
          />
        </div>
      )}
    </Sider>
  );
};

export default Sidebar;
